import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import {
  getCustomerById,
  getCustomers,
  getCustomerAvailableSites,
  updateCustomerAssignment,
} from '../utils/storage';
import { stripClientId } from './CustomersPage';
import LoadingScreen from './LoadingScreen';
import './EquipmentDetailModal.css';   // reuse the equipment modal chrome
import './CustomersPage.css';
import './Modal.css';

export default function CustomerDetail() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { setBreadcrumb, clearBreadcrumb } = useBreadcrumb();

  const [customer, setCustomer] = useState(null);
  const [neighbours, setNeighbours] = useState({ prevId: null, nextId: null });
  const [loading, setLoading] = useState(true);
  const [pageReady, setPageReady] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    const [c, list] = await Promise.all([getCustomerById(customerId), getCustomers()]);
    setCustomer(c);
    // Neighbours follow the same Client ID A→Z order as the table.
    const items = [...((list && list.items) || [])].sort((a, b) =>
      stripClientId(a.client_code).localeCompare(stripClientId(b.client_code))
    );
    const idx = items.findIndex((x) => String(x.id) === String(customerId));
    setNeighbours({
      prevId: idx > 0 ? items[idx - 1].id : null,
      nextId: idx >= 0 && idx < items.length - 1 ? items[idx + 1].id : null,
    });
    setLoading(false);
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (customer) {
      setBreadcrumb([
        { label: 'Customers', path: '/customers' },
        { label: customer.company_name || 'Customer', path: null },
      ]);
    }
    return () => clearBreadcrumb();
  }, [customer, setBreadcrumb, clearBreadcrumb]);

  if (!pageReady) {
    return <LoadingScreen isLoading={loading} onDone={() => setPageReady(true)} />;
  }

  if (!customer) {
    return (
      <div className="page-container">
        <button className="btn-back" onClick={() => navigate('/customers')}>&larr; Back to Customers</button>
        <p style={{ marginTop: 'var(--space-lg)' }}>Customer not found.</p>
      </div>
    );
  }

  const addr = [customer.line1, customer.line2, customer.postal_code, customer.city, customer.country]
    .filter(Boolean).join(', ');
  const projects = customer.project_codes || [];
  const { prevId, nextId } = neighbours;

  const assignmentText = customer.assigned_client_id
    ? (customer.assigned_client_name || customer.assigned_client_id)
    : customer.muted
      ? 'Muted (excluded from the pending count)'
      : 'Not assigned';

  return (
    <div className="page-container">
      <button className="btn-back" onClick={() => navigate('/customers')}>&larr; Back to Customers</button>

      <header className="detail-header">
        <div className="detail-header-top">
          <h1>{customer.company_name || 'Customer'}</h1>
          <div className="header-actions">
            <div className="nav-chevrons">
              {prevId ? (
                <button className="nav-chevron" onClick={() => navigate(`/customers/${prevId}`)} title="Previous customer">‹</button>
              ) : <span className="nav-chevron-hidden">‹</span>}
              {nextId ? (
                <button className="nav-chevron" onClick={() => navigate(`/customers/${nextId}`)} title="Next customer">›</button>
              ) : <span className="nav-chevron-hidden">›</span>}
            </div>
          </div>
        </div>

        <div className="detail-info">
          <div className="od-info-header-row">
            <div className="od-info-title" style={{ marginBottom: 0 }}>Customer Information</div>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowModal(true)}>
              {customer.assigned_client_id ? 'Change test site' : 'Assign test site'}
            </button>
          </div>
          <p>Client ID: {stripClientId(customer.client_code) || '—'}</p>
          <p>Client Code: {customer.client_code || '—'}</p>
          <p>Address: {addr || '—'}</p>
          <p>Country: {customer.country || '—'}</p>
          <p>Variable site locations: {customer.has_variable_site_locations ? 'Yes' : 'No'}</p>
          <p>Project codes ({projects.length}): {projects.length ? projects.join(', ') : '—'}</p>
          <p>
            Test site:{' '}
            <strong className={!customer.assigned_client_id && !customer.muted ? 'cust-pending-note' : undefined}>
              {assignmentText}
            </strong>
          </p>
        </div>
      </header>

      {showModal && (
        <AssignSiteModal
          customer={customer}
          onClose={() => setShowModal(false)}
          onSaved={(updated) => { setCustomer(updated); setShowModal(false); }}
        />
      )}
    </div>
  );
}

/* ── Assign Test Site modal (equipment-modal template) ────────────── */
function AssignSiteModal({ customer, onClose, onSaved }) {
  const customerId = customer.id;
  const [mode, setMode] = useState(customer.muted ? 'mute' : 'assign');
  const [sites, setSites] = useState([]);
  const [selected, setSelected] = useState(customer.assigned_client_id || '');
  const [loadingSites, setLoadingSites] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getCustomerAvailableSites(customerId)
      .then((s) => { setSites(s); setLoadingSites(false); })
      .catch(() => { setError('Failed to load test sites.'); setLoadingSites(false); });
  }, [customerId]);

  async function handleSave() {
    if (saving) return;
    setSaving(true); setError('');
    try {
      let updated;
      if (mode === 'mute') {
        updated = await updateCustomerAssignment(customerId, { muted: true });
      } else if (selected && selected !== customer.assigned_client_id) {
        updated = await updateCustomerAssignment(customerId, { muted: false, setClient: true, clientId: selected });
      } else {
        updated = await updateCustomerAssignment(customerId, { muted: false });
      }
      onSaved(updated);
    } catch (err) {
      setError(err?.message || 'Could not save.');
      setSaving(false);
    }
  }

  async function handleUnassign() {
    if (saving) return;
    setSaving(true); setError('');
    try {
      onSaved(await updateCustomerAssignment(customerId, { muted: false, setClient: true, clientId: null }));
    } catch (err) {
      setError(err?.message || 'Could not unassign.');
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal equipment-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 className="equipment-detail-heading"><span>Assign Test Site</span></h2>
            <div className="equipment-detail-sub">{customer.company_name}</div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="equipment-detail-form">
          {mode === 'mute' ? (
            <p className="confirm-text cust-muted-note">
              Muted customers are excluded from the pending count. Any linked test site is kept and
              restored when you switch back to Assign.
            </p>
          ) : loadingSites ? (
            <div className="ddl-load-hint">Loading test sites…</div>
          ) : sites.length === 0 ? (
            <p className="confirm-text">No available test sites — every site is already linked to another customer.</p>
          ) : (
            <div className="form-group">
              <label>Test site</label>
              <div className="cust-site-list">
                {sites.map((s) => {
                  const isSel = selected === s.id;
                  return (
                    <div
                      key={s.id}
                      className={`cust-site-item${isSel ? ' selected' : ''}`}
                      onClick={() => setSelected(s.id)}
                    >
                      <span className="cust-site-text">
                        <span className="cust-site-name">{s.name}</span>
                        {s.client_name && <span className="cust-site-meta">{s.client_name}</span>}
                      </span>
                      {isSel
                        ? <span className="cust-site-check" title="Selected">✓</span>
                        : <span className="cust-site-arrow">›</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {error && <div className="equipment-error">{error}</div>}

        <div className="equipment-detail-footer">
          {/* Single mode toggle — flips Assign ⇄ Mute (applied on Save). */}
          <button
            className="btn btn-secondary"
            onClick={() => setMode((m) => (m === 'assign' ? 'mute' : 'assign'))}
            disabled={saving}
            title="Toggle between Assign and Mute"
          >
            {mode === 'assign' ? 'Switch to Mute' : 'Switch to Assign'}
          </button>
          {customer.assigned_client_id && (
            <button className="btn btn-danger" onClick={handleUnassign} disabled={saving}>
              Unassign
            </button>
          )}
          <div className="equipment-detail-footer-spacer" />
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
